import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';
import ChartTypePreviewSvg from './chartBuilder/ChartTypePreviewSvg';
import ChartBuilderQuestionExamplesPanel from './chartBuilder/ChartBuilderQuestionExamplesPanel';

const CHART_TYPE_OPTIONS = [
  { value: 'table', label: 'Tabla' },
  { value: 'bar', label: 'Barras' },
  { value: 'line', label: 'Líneas' },
  { value: 'pie', label: 'Torta' },
  { value: 'radar', label: 'Radar (spider)' },
  { value: 'kpi', label: 'KPI' },
];

const emptyImages = () =>
  CHART_TYPE_OPTIONS.reduce((acc, ct) => {
    acc[ct.value] = '';
    return acc;
  }, {});

const ChartBuilderReferenceAdmin = ({ open, getAuthHeaders, embedded = false }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState(emptyImages);

  const loadReferencesMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/admin/chart-builder-references', { headers }),
  });

  const loadReferences = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setLoading(true);
    try {
      const res = await loadReferencesMutation.mutateAsync(headers);
      if (res.data.success) {
        setImages({ ...emptyImages(), ...(res.data.images || {}) });
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Error al cargar referencias');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, showError]);

  useEffect(() => {
    if (open) loadReferences();
  }, [open, loadReferences]);

  const handleFile = (chartType, event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      showError('Selecciona un archivo de imagen');
      return;
    }
    if (file.size > 800 * 1024) {
      showError('La imagen debe pesar menos de 800 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImages((prev) => ({ ...prev, [chartType]: reader.result || '' }));
    };
    reader.readAsDataURL(file);
  };

  const clearImage = (chartType) => {
    setImages((prev) => ({ ...prev, [chartType]: '' }));
  };

  const saveReferencesMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.put('/api/admin/chart-builder-references', body, { headers }),
  });

  const handleSave = async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setSaving(true);
    try {
      await saveReferencesMutation.mutateAsync({ body: { images }, headers });
      showSuccess('Referencias de gráficos guardadas');
    } catch (err) {
      showError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: embedded ? 0 : 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sube una imagen de referencia por tipo de gráfico. Se muestra en el builder guiado al elegir el
        tipo; si no hay imagen personalizada, se usa una miniatura predeterminada.
      </Typography>
      <Grid container spacing={2}>
        {CHART_TYPE_OPTIONS.map((ct) => (
          <Grid item xs={12} sm={6} md={4} key={ct.value}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {ct.label}
              </Typography>
              <Box
                sx={{
                  height: 96,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  px: 1,
                }}
              >
                {images[ct.value] ? (
                  <Box
                    component="img"
                    src={images[ct.value]}
                    alt={`Referencia ${ct.label}`}
                    sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Box sx={{ width: '70%', height: '70%' }}>
                    <ChartTypePreviewSvg type={ct.value} />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                  Subir
                  <input hidden type="file" accept="image/*" onChange={(e) => handleFile(ct.value, e)} />
                </Button>
                {images[ct.value] && (
                  <IconButton size="small" onClick={() => clearImage(ct.value)} aria-label={`Quitar ${ct.label}`}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar referencias'}
        </Button>
      </Box>

      <ChartBuilderQuestionExamplesPanel
        defaultExpanded
        getAuthHeaders={getAuthHeaders}
        intro="Incluye ejemplos de referencia del producto y los que hayas guardado desde Vista previa → Guardar como ejemplo. Puedes eliminar los guardados aquí."
      />
    </Box>
  );
};

export default ChartBuilderReferenceAdmin;
